import Share from '@/assets/icons/share.svg?react'
import { routes } from '@/pages'
import { Button, Text, Tooltip, TooltipContent, TooltipTrigger } from '@/ui-kit'
import { formatBytes, formatNumber } from '@/utils'
import clsx from 'clsx'
import { Clock, CloudUpload, Star, Users, Zap } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mission, TYPE_TEXT_MAP } from '../api/models'
import { useMissionsUrl } from '../context/MissionsUrlProvider'
import { MissionBadge, STATUS_TEXT_MAP, getBadgeVariant } from './MissionBadge'
import { MissionCardMainActionButton } from './MissionCardMainButton'

type MissionCardCollapsedProps = {
  mission: Mission
  basePath?: string
  highlighted?: boolean
}

export const MissionCard = ({ mission, highlighted = false }: MissionCardCollapsedProps) => {
  const { missionsUrl } = useMissionsUrl()
  const [copied, setCopied] = useState(false)

  const detailPath =
    mission.type === 'STAKING' ? routes.stake.path : (mission.detailPath ?? `${missionsUrl}/${mission.id}`)

  const shareURL =
    mission.type === 'STAKING'
      ? `${window.location.origin}${missionsUrl}?missionId=${mission.id}`
      : `${window.location.origin}${detailPath}`

  const handleShare = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    void navigator.clipboard.writeText(shareURL).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }
  const isVoiceMission = ['VOICE_CONVERSATION', 'AUDIO_RECORDING'].includes(mission.type)

  return (
    <li
      id={`mission-card-${mission.id}`}
      className={clsx('cursor-pointer rounded-2xl border border-solid p-4 transition-all', 'min-h-[300px]', {
        'hover:border-orange border-gray-100': mission.availability.available,
        'border-gray-100 hover:border-gray-200/50': !mission.availability.available,
        'border-green hover:border-green!':
          mission.view.latestSubmissionStatus === 'COMPLETED' && !mission.view.nextSubmissionAvailable,
        'ring-orange ring-offset-background z-1 shadow-[0_0_18px_6px_color-mix(in_srgb,var(--color-orange)_42%,transparent),0_0_28px_10px_color-mix(in_srgb,var(--color-orange)_22%,transparent)] ring-2 ring-offset-1':
          highlighted
      })}
    >
      <Link className="flex h-full flex-col gap-4" to={detailPath || '#'}>
        <div className="flex justify-between gap-1">
          <div className="flex gap-1">
            {!mission.availability.available ? (
              <MissionBadge>{'Unavailable'}</MissionBadge>
            ) : (
              <>
                {mission.tags.length && (isVoiceMission || !mission.view.latestSubmissionStatus) ? (
                  <MissionBadge variant={'warning'}>{mission.tags[0]}</MissionBadge>
                ) : (
                  <MissionBadge variant={getBadgeVariant(mission)} tooltip={mission.view.tooltip}>
                    {mission.view.latestSubmissionStatus === 'SUBMITTED' ? (
                      <>
                        <Clock size={15} />
                        {STATUS_TEXT_MAP.SUBMITTED}
                      </>
                    ) : mission.view.latestSubmissionStatus === 'COMPLETED' && mission.view.nextSubmissionAvailable ? (
                      'Attention'
                    ) : (
                      STATUS_TEXT_MAP[mission.view.latestSubmissionStatus || 'null'] || 'New'
                    )}
                  </MissionBadge>
                )}
              </>
            )}

            <MissionBadge variant="default" className="whitespace-normal">
              <div className="block overflow-hidden text-ellipsis">{TYPE_TEXT_MAP[mission.type] || mission.type}</div>
            </MissionBadge>
          </div>
          {mission.reward > 0 && (
            <MissionBadge variant="success" className="max-mobile:hidden">
              <Star size={15} /> {formatNumber(mission.reward, 0)} points
            </MissionBadge>
          )}
        </div>
        <div className="flex grow flex-col justify-between gap-1">
          <div className="flex flex-col gap-2">
            <Text type="subtitle-semibold" className={clsx('text-left transition-colors')}>
              {mission.title}
            </Text>
            <Text type="body2-regular" className="text-gray-90 line-clamp-3">
              {mission.shortDescription}
            </Text>
          </div>
          <div className="py-6">
            {mission.view.accruedReward > 0 || (mission.type === 'WEBSITE_LOGIN' && mission.view.earnedPoints > 0) ? (
              <>
                <div className="text-gray-70 flex items-start gap-2">
                  <Star size={16} className="text-green mt-1 shrink-0" />
                  You got {formatNumber(mission.view.accruedReward, 0)} points
                </div>
                {mission.type === 'WEBSITE_LOGIN' && mission.view.earnedPoints > 0 && (
                  <div className="text-gray-70 flex items-start gap-2">
                    <Zap size={16} className="text-green mt-1 shrink-0" />
                    You earned extra {formatNumber(mission.view.earnedPoints, 0)} points
                  </div>
                )}
              </>
            ) : (
              <>
                {mission.reward > 0 && (
                  <div className="text-gray-70 flex items-start gap-2">
                    <Star size={16} className="text-green mt-1 shrink-0" />
                    Earn {formatNumber(mission.reward, 0)} Points for completing the mission
                  </div>
                )}
                {mission.type === 'WEBSITE_LOGIN' && mission.view.multiplier > 1 && (
                  <div className="text-gray-70 flex items-start gap-2">
                    <Zap size={16} className="text-green mt-1 shrink-0" />
                    Earn x{mission.view.multiplier} points on jobs
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex gap-6">
              <div className="text-gray-90 flex items-center gap-2">
                <Users size={15} className="text-gray-80 shrink-0" />
                <Text type="body2-regular">{formatNumber(mission.view.joined, 0)} joined</Text>
              </div>
              {mission.type === 'FILE_UPLOAD' &&
                mission.view.latestSubmissionStatus &&
                mission.view.uploadedBytes > 0 && (
                  <div className="text-gray-90 flex items-center gap-2">
                    <CloudUpload size={15} className="text-gray-80 shrink-0" />
                    <Text type="body2-regular">Uploaded {formatBytes(mission.view.uploadedBytes)}</Text>
                  </div>
                )}
              {mission.type === 'WEBSITE_LOGIN' && mission.view.executedJobs > 0 && (
                <div className="text-gray-90 flex items-center gap-2">
                  <CloudUpload size={15} className="text-gray-80 shrink-0" />
                  <Text type="body2-regular">{formatNumber(mission.view.executedJobs, 0)} Executed Jobs</Text>
                </div>
              )}
            </div>
            <div className="relative mt-2 flex gap-2">
              <MissionCardMainActionButton mission={mission} />
              <Tooltip open={copied} onOpenChange={(open) => !open && setCopied(false)}>
                <TooltipTrigger className="w-full">
                  <Button
                    type="button"
                    className="relative flex w-full gap-2"
                    variant="bordered-gray"
                    size="small"
                    onClick={handleShare}
                  >
                    <Share /> Share
                  </Button>
                </TooltipTrigger>
                <TooltipContent align="end" sideOffset={4}>
                  Copied!
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </Link>
    </li>
  )
}
